import React, { Suspense, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import InstallAppButton from './InstallAppButton';
import TrialOfferStrip from './TrialOfferStrip';
import {
  BookOpen, Search, Headphones, Layers, Network, Library, Languages, ScrollText,
  PenTool, Presentation, FileDown, Flame, Gamepad2, LayoutDashboard, Image as ImageIcon,
  Users, Sparkles, ArrowRight, ArrowUpRight, Quote, Map, Loader2, Compass,
  HeartHandshake, MessageCircle, Images, Star,
} from 'lucide-react';

// Journey maps (3D Globe ⇄ 3D Terrain) behind a view toggle — lazy so the
// heavy 3D engines stay out of the initial paint.
const Journeys3DGlobe = React.lazy(() => import('./JourneysExperience'));

const landingStyles = `
@keyframes soundbar { 0%,100%{transform:scaleY(0.18)} 50%{transform:scaleY(1)} }
.soundbar{ transform-origin:bottom; animation:soundbar 1.3s ease-in-out infinite; }
`;

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

// Scroll-reveal helper (elegant, slow fade-up)
const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, className = '' }) => (
  <span className={`font-plexmono text-xs uppercase tracking-[0.22em] text-slate-500 ${className}`}>{children}</span>
);

const GlobePlaceholder = () => (
  <div className="flex h-[58vh] min-h-[420px] items-center justify-center border border-slate-200 bg-[#0F1A2E]">
    <div className="text-center text-slate-400">
      <Loader2 className="mx-auto mb-3 h-7 w-7 animate-spin text-[#D4AF37]" />
      <p className="font-plexmono text-xs uppercase tracking-widest">Loading the journeys</p>
    </div>
  </div>
);

// Flat brutalist feature card
const BentoCard = ({ icon: Icon, eyebrow, title, children, className = '', accent = '#B0871F', testid, dark = false }) => (
  <div
    data-testid={testid}
    className={`group flex flex-col gap-3 border p-7 sm:p-8 transition-colors duration-300 ${
      dark
        ? 'border-slate-700 bg-[#0F1A2E] hover:border-[#D4AF37]/50'
        : 'border-slate-200 bg-white hover:border-slate-400'
    } ${className}`}
  >
    <div className="flex items-center gap-3">
      <span
        className="inline-flex h-10 w-10 items-center justify-center border"
        style={{ borderColor: dark ? '#2A3550' : '#E2E8F0', color: accent }}
      >
        <Icon className="h-5 w-5" />
      </span>
      {eyebrow && <Overline className={dark ? 'text-slate-400' : ''}>{eyebrow}</Overline>}
    </div>
    <h3 className={`font-display text-2xl leading-tight md:text-3xl ${dark ? 'text-[#F6F8FB]' : 'text-slate-900'}`}>{title}</h3>
    <div className={`font-plex text-[15px] leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{children}</div>
  </div>
);

const NewLandingPage = () => {
  const navigate = useNavigate();

  const [testimonials, setTestimonials] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`)
      .then((r) => { if (!cancelled) setTestimonials(r.data?.testimonials || []); })
      .catch(() => {});
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/community/landing-stats`)
      .then((r) => { if (!cancelled) setCommunityStats(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const studyFeatures = [
    { icon: Layers, eyebrow: 'Verse Lens', title: 'One tap. Every source.', span: 'md:col-span-8 md:row-span-2', accent: '#B0871F',
      body: 'Touch any verse and a single panel gathers commentary, cross-references, original-language word studies and maps — the whole library, aggregated for that one line of Scripture.' },
    { icon: Compass, eyebrow: 'Factbook', title: 'Every person, place & thing.', span: 'md:col-span-4', accent: '#2A6F77',
      body: 'A complete encyclopedia of biblical entities, cross-checked against ISBE & Nave\u2019s.' },
    { icon: Network, eyebrow: 'Treasury of Scripture Knowledge', title: '344,000+ cross-references.', span: 'md:col-span-4', accent: '#B0871F',
      body: 'Let Scripture interpret Scripture — the classic TSK web, surfaced instantly.' },
    { icon: Library, eyebrow: 'Classic Commentaries', title: 'Henry, Gill, JFB & Clarke.', span: 'md:col-span-8', accent: '#2A6F77',
      body: 'Over 15,000 chapters of trusted, time-tested exposition — alongside Strong\u2019s Hebrew & Greek and a 9,000-article Bible encyclopedia.' },
  ];

  const dailyFeatures = [
    { icon: Flame, eyebrow: 'Habit', title: 'Streaks & Plans', body: 'AI-built or custom reading plans with streak tracking that keeps you in the Word.' },
    { icon: Gamepad2, eyebrow: 'Play', title: 'Devotional Games', body: 'Bible trivia, \u201CWho Said It\u201D and daily word challenges that make learning stick.' },
    { icon: ImageIcon, eyebrow: 'Share', title: 'Verse Art & Community', body: 'Craft shareable verse images and follow what\u2019s moving the community today.' },
  ];

  const stats = [
    { n: '344K+', l: 'Cross-references' },
    { n: '15K+', l: 'Commentary entries' },
    { n: '14K+', l: "Strong\u2019s word studies" },
    { n: '19', l: 'Bible translations' },
  ];

  return (
    <div className="relative min-h-screen bg-[#F6F8FB] font-plex text-slate-900 antialiased pb-20 lg:pb-0">
      <Helmet>
        <title>Lumera | Experience the Living Word</title>
        <meta name="description" content="An AI-powered Bible study suite: immersive audio, Verse Lens, classic commentaries, 344,000+ cross-references, Strong's word studies and interactive 3D Biblical journeys." />
        <meta name="theme-color" content="#F6F8FB" />
        <style>{landingStyles}</style>
      </Helmet>

      {/* tactile paper grain */}
      <div className="pointer-events-none fixed inset-0 z-[5] mix-blend-multiply opacity-[0.05]" style={{ backgroundImage: GRAIN }} />

      {/* ───────────── NAV ───────────── */}
      <header data-testid="nav-header" className="sticky top-0 z-50 border-b border-slate-200 bg-[#F6F8FB]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <button onClick={() => navigate('/')} className="flex items-baseline gap-2">
            <span className="font-display text-2xl font-semibold tracking-tight text-slate-900">Lumera</span>
            <span className="hidden font-plexmono text-[10px] uppercase tracking-[0.2em] text-slate-400 sm:inline">Living Word</span>
          </button>
          <nav className="hidden items-center gap-8 font-plex text-sm text-slate-600 lg:flex">
            <a href="#study" className="transition-colors hover:text-[#B0871F]">Study</a>
            <a href="#journeys" className="transition-colors hover:text-[#B0871F]">Journeys</a>
            <a href="#daily" className="transition-colors hover:text-[#B0871F]">Daily</a>
            <button onClick={() => navigate('/explore')} data-testid="nav-community" className="transition-colors hover:text-[#B0871F]">Community</button>
          </nav>
          <div className="flex items-center gap-3">
            <InstallAppButton
              testId="nav-install"
              label="Install App"
              className="hidden items-center gap-1.5 border border-slate-300 px-4 py-2.5 font-plex text-sm font-medium text-slate-800 transition-colors hover:border-[#B0871F] hover:text-[#B0871F] md:inline-flex"
            />
            <button onClick={() => navigate('/login')} data-testid="nav-signin" className="font-plex text-sm font-medium text-slate-700 transition-colors hover:text-slate-900">Sign in</button>
            <button onClick={() => navigate('/bible')} data-testid="nav-cta" className="bg-[#B0871F] px-5 py-2.5 font-plex text-sm font-medium text-[#0F1A2E] transition-colors hover:bg-[#9A731A]">Open the Word</button>
          </div>
        </div>
      </header>

      {/* ───────────── HERO ───────────── */}
      <section data-testid="hero-section" id="journeys" className="relative border-b border-slate-200">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 sm:px-6 py-10 sm:py-14 md:px-12 lg:grid-cols-12 lg:gap-12 lg:py-24">
          <div className="lg:col-span-5">
            <Reveal>
              <div data-testid="hero-trust-pill" className="mb-4 flex w-fit items-center gap-2 rounded-full border border-[#B0871F]/30 bg-[#B0871F]/10 px-3.5 py-1.5">
                <span className="flex text-[#B0871F]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
                  ))}
                </span>
                <span className="font-plex text-xs font-medium tracking-wide text-slate-700">Loved by believers worldwide · Free to start</span>
              </div>
              <Overline>AI-powered scripture study</Overline>
              <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-tight text-slate-900 sm:text-5xl sm:leading-[0.95] md:text-6xl lg:text-7xl">
                Experience the
                <span className="block italic text-[#B0871F]">Living Word.</span>
              </h1>
              <p className="mt-5 max-w-md font-plex text-base leading-relaxed text-slate-600 sm:text-lg">
                Read, study, and <span className="text-slate-800">hear</span> the Bible with AI insights, classic commentaries, and interactive 3D journeys — in one quiet, beautiful place.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button onClick={() => navigate('/bible')} data-testid="hero-cta-primary" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B0871F] px-7 py-3.5 font-plex font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5 hover:bg-[#9A731A]">
                  Open the Word <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={() => navigate('/read?ref=John 3:16')} data-testid="hero-cta-secondary" className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 font-plex font-medium text-slate-800 transition-colors hover:bg-slate-100">
                  Read a verse free
                </button>
                <button onClick={() => navigate('/explore')} data-testid="hero-cta-community" className="hidden items-center justify-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 font-plex font-medium text-slate-800 transition-colors hover:bg-slate-100 sm:inline-flex">
                  Explore Community
                </button>
              </div>
              <TrialOfferStrip language="en" variant="landing" />
              <p className="mt-7 border-t border-slate-200 pt-5 font-display text-base italic text-slate-500 sm:text-lg">
                &ldquo;Your word is a lamp to my feet and a light to my path.&rdquo;
                <span className="mt-1 block font-plexmono text-[11px] not-italic uppercase tracking-widest text-[#B0871F]">Psalm 119:105</span>
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {/* Mobile/tablet: lightweight poster — avoids loading the heavy WebGL globe */}
            <div className="lg:hidden" data-testid="hero-visual-mobile">
              <img
                src="https://images.unsplash.com/photo-1604882737206-8a000c03d8fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxwZXJzb24lMjByZWFkaW5nJTIwYmlibGV8ZW58MHx8fHwxNzkwODc2MTM2fDA&ixlib=rb-4.1.0&q=85"
                alt="Reading Scripture in a quiet moment"
                loading="lazy"
                className="h-[36vh] w-full rounded-2xl object-cover shadow-lg"
              />
              <p className="mt-3 flex items-center gap-2 font-plexmono text-[11px] uppercase tracking-widest text-slate-400">
                <Map className="h-3.5 w-3.5" /> Interactive 3D journeys · open on desktop to explore
              </p>
            </div>
            {/* Desktop: interactive globe */}
            <div className="hidden lg:block">
              <Reveal delay={0.15}>
                <Suspense fallback={<GlobePlaceholder />}>
                  <Journeys3DGlobe />
                </Suspense>
                <p className="mt-3 flex items-center gap-2 font-plexmono text-[11px] uppercase tracking-widest text-slate-400">
                  <Map className="h-3.5 w-3.5" /> Interactive · Drag to explore · 7 biblical journeys
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── COMMUNITY SPOTLIGHT (bento) ───────────── */}
      <section data-testid="community-spotlight-section" id="community" className="border-b border-slate-200 bg-[#F7F5F0]">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 lg:py-24">
          <Reveal className="mb-12 max-w-2xl">
            <Overline>The fellowship</Overline>
            <h2 className="mt-4 font-display text-4xl font-medium leading-none tracking-tight text-slate-900 md:text-5xl">
              Together in the Word — <span className="italic text-[#B0871F]">fellowship that feels alive.</span>
            </h2>
            <p className="mt-4 font-plex text-lg text-slate-600">
              Pray for one another, keep each other accountable, and share the journey. Lumera is a community, not just a library.
            </p>
            {communityStats && (
              <div data-testid="community-proof-strip" className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3">
                {[
                  communityStats.believers > 0 && { n: communityStats.believers, label: 'believers growing together' },
                  communityStats.prayers > 0 && { n: communityStats.prayers, label: 'prayers shared' },
                  communityStats.prayers_answered > 0 && { n: communityStats.prayers_answered, label: 'answered' },
                  communityStats.stories > 0 && { n: communityStats.stories, label: 'stories shared' },
                  communityStats.testimonies > 0 && { n: communityStats.testimonies, label: 'testimonies' },
                ].filter(Boolean).map((s) => (
                  <div key={s.label} className="flex items-baseline gap-2">
                    <span className="font-display text-3xl font-medium text-[#B0871F]">{s.n.toLocaleString()}</span>
                    <span className="font-plexmono text-[11px] uppercase tracking-widest text-slate-500">{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </Reveal>

          <div className="grid auto-rows-[minmax(150px,auto)] grid-cols-1 gap-6 md:grid-cols-12">
            {/* Prayer Wall — large hero card */}
            <Reveal className="md:col-span-7 md:row-span-2 flex">
              <div data-testid="community-prayer-wall" className="group relative w-full overflow-hidden rounded-2xl border border-slate-200 shadow-sm min-h-[320px]">
                <img src="https://images.unsplash.com/photo-1662151855613-f2ff53199385?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="Believers praying together" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E]/90 via-[#0F1A2E]/40 to-transparent" />
                <div className="absolute bottom-0 p-7 text-[#F6F8FB]">
                  <HeartHandshake className="h-7 w-7 text-[#E9C46A]" />
                  <h3 className="mt-3 font-display text-2xl font-medium md:text-3xl">Prayer Wall</h3>
                  <p className="mt-2 max-w-md font-plex text-sm text-slate-200">
                    Post a request, pray over others, and watch answered prayers roll in. Nobody walks alone.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Messaging */}
            <Reveal delay={0.08} className="md:col-span-5 flex">
              <div data-testid="community-messaging" className="w-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <MessageCircle className="h-6 w-6 text-[#2A6F77]" />
                <h3 className="mt-3 font-display text-xl font-medium text-slate-900">1:1 &amp; Group Messaging</h3>
                <p className="mt-2 font-plex text-sm text-slate-600">
                  Start a private conversation or a study group — share verses, notes and encouragement in real time.
                </p>
              </div>
            </Reveal>

            {/* Accountability streaks */}
            <Reveal delay={0.16} className="md:col-span-5 flex">
              <div data-testid="community-streaks" className="w-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <Flame className="h-6 w-6 text-[#C26D5C]" />
                <h3 className="mt-3 font-display text-xl font-medium text-slate-900">Accountability Streaks</h3>
                <p className="mt-2 font-plex text-sm text-slate-600">
                  Pair with a partner and keep a shared reading streak alive. Grow a daily habit that actually sticks.
                </p>
              </div>
            </Reveal>

            {/* Stories & Testimonies */}
            <Reveal delay={0.1} className="md:col-span-4 flex">
              <div data-testid="community-stories" className="w-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <Sparkles className="h-6 w-6 text-[#B0871F]" />
                <h3 className="mt-3 font-display text-xl font-medium text-slate-900">Stories &amp; Testimonies</h3>
                <p className="mt-2 font-plex text-sm text-slate-600">
                  Share what God is doing — in a 24-hour Story or a lasting testimony that lifts the whole community.
                </p>
              </div>
            </Reveal>

            {/* Explore / photo feed */}
            <Reveal delay={0.18} className="md:col-span-8 flex">
              <div data-testid="community-explore" className="group relative w-full overflow-hidden rounded-2xl border border-slate-200 shadow-sm min-h-[200px]">
                <img src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="Worship together" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent" />
                <div className="relative p-7 max-w-sm">
                  <Images className="h-6 w-6 text-[#2A6F77]" />
                  <h3 className="mt-3 font-display text-xl font-medium text-slate-900">Explore Feed</h3>
                  <p className="mt-2 font-plex text-sm text-slate-600">
                    A visual feed of verse art, reflections and moments — discover and share what's inspiring believers today.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <button
              onClick={() => navigate('/login')}
              data-testid="community-cta-join"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C26D5C] px-8 py-3.5 font-plex font-medium text-white transition-colors hover:bg-[#A85B4B]"
            >
              Join the community — free <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate('/explore')}
              data-testid="community-cta-explore"
              className="inline-flex items-center gap-2 font-plex font-medium text-slate-800 transition-colors hover:text-[#B0871F]"
            >
              Take a look inside <ArrowUpRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ───────────── STUDY DEPTH (bento) ───────────── */}
      <section data-testid="study-depth-section" id="study" className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 lg:py-24">
          <Reveal className="mb-12 max-w-2xl">
            <Overline>The depths</Overline>
            <h2 className="mt-4 font-display text-4xl font-medium leading-none tracking-tight text-slate-900 md:text-5xl">
              Logos-level study, <span className="italic text-[#2A6F77]">seamlessly woven in.</span>
            </h2>
            <p className="mt-4 font-plex text-lg text-slate-600">Every tool a serious student needs — none of the overwhelm.</p>
          </Reveal>
          <div className="grid auto-rows-[minmax(150px,auto)] grid-cols-1 gap-6 md:grid-cols-12">
            {studyFeatures.map((f, i) => (
              <Reveal key={f.eyebrow} delay={i * 0.08} className={`flex ${f.span}`}>
                <BentoCard testid={`study-card-${i}`} icon={f.icon} eyebrow={f.eyebrow} title={f.title} accent={f.accent} className="w-full">
                  {f.body}
                </BentoCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── AUDIO (dark contrast block) ───────────── */}
      <section data-testid="audio-bible-section" className="bg-[#0F1A2E] text-[#F6F8FB]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-12 md:px-12 lg:grid-cols-2 lg:py-24">
          <Reveal>
            <Overline className="text-[#D4AF37]">Immersive audio</Overline>
            <h2 className="mt-4 font-display text-4xl font-medium leading-none tracking-tight md:text-5xl lg:text-6xl">
              Hear it. Don&apos;t<br /> just read it.
            </h2>
            <p className="mt-6 max-w-md font-plex text-lg leading-relaxed text-slate-400">
              Let Scripture wash over you with cinematic ambient soundscapes — peaceful waters, holy fire, rolling storms — across 19 translations. Perfect for devotions, commutes and bedtime.
            </p>
            <button onClick={() => navigate('/bible')} className="mt-8 inline-flex items-center gap-2 border border-slate-600 px-7 py-3.5 font-plex font-medium text-[#F6F8FB] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]">
              <Headphones className="h-4 w-4" /> Start listening
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex h-56 items-end justify-center gap-1.5 sm:gap-2">
              {Array.from({ length: 48 }).map((_, i) => (
                <div
                  key={i}
                  className="soundbar w-1.5 flex-1 rounded-full sm:w-2"
                  style={{
                    backgroundColor: i % 7 === 0 ? '#D4AF37' : '#2A3550',
                    height: `${30 + (i % 6) * 12}%`,
                    animationDelay: `${(i % 12) * 0.09}s`,
                  }}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── AI TOOLS (split) ───────────── */}
      <section data-testid="ai-tools-section" className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 lg:py-24">
          <Reveal className="mb-12 max-w-2xl">
            <Overline>Quiet intelligence</Overline>
            <h2 className="mt-4 font-display text-4xl font-medium leading-none tracking-tight text-slate-900 md:text-5xl">
              A companion for the heart, <span className="italic text-[#B0871F]">a scribe for the desk.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Pastoral companion */}
            <Reveal>
              <div className="flex h-full flex-col gap-4 border border-slate-200 bg-white p-8">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center border border-slate-200 text-[#B0871F]"><Search className="h-5 w-5" /></span>
                  <Overline>AI Pastoral Companion</Overline>
                </div>
                <h3 className="font-display text-2xl text-slate-900 md:text-3xl">Ask anything. Be met with grace.</h3>
                <div className="mt-2 space-y-3">
                  <div className="ml-auto w-fit max-w-[80%] bg-[#15233E] px-4 py-2 font-plex text-sm text-white">I feel anxious about the future.</div>
                  <div className="w-fit max-w-[90%] border-l-2 border-[#2A6F77] bg-slate-50 px-4 py-3 font-plexmono text-[13px] leading-relaxed text-slate-700">
                    &ldquo;Do not be anxious about anything&hellip;&rdquo; — Philippians 4:6–7 invites you to bring every worry to God in prayer, and promises a peace that guards your heart.
                  </div>
                </div>
              </div>
            </Reveal>
            {/* Sermon scaffolding */}
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col gap-4 border border-slate-200 bg-white p-8">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center border border-slate-200 text-[#2A6F77]"><PenTool className="h-5 w-5" /></span>
                  <Overline>AI Sermon Scaffolding</Overline>
                </div>
                <h3 className="font-display text-2xl text-slate-900 md:text-3xl">From a passage to a pulpit-ready outline.</h3>
                <div className="mt-2 border border-slate-200 bg-[#0F1A2E] p-5 font-plexmono text-[13px] leading-relaxed text-slate-300">
                  <span className="text-[#D4AF37]">// Generated outline — &ldquo;Follow Me&rdquo;</span><br />
                  I. The Call &middot; Matthew 4:18–22<br />
                  II. The Cost &middot; Luke 9:23<br />
                  III. The Commission &middot; Matthew 28:19
                </div>
                <div className="mt-1 flex flex-wrap gap-2 font-plexmono text-[11px] text-slate-500">
                  <span className="border border-slate-200 px-2 py-1">Companion PDF</span>
                  <span className="border border-slate-200 px-2 py-1">SlideCon slides</span>
                  <span className="border border-slate-200 px-2 py-1">Embed widget</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────── DAILY RHYTHM (bento) ───────────── */}
      <section data-testid="daily-rhythm-section" id="daily" className="border-b border-slate-200 bg-[#EBEEF3]">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 lg:py-24">
          <Reveal className="mb-12 max-w-2xl">
            <Overline>The rhythm</Overline>
            <h2 className="mt-4 font-display text-4xl font-medium leading-none tracking-tight text-slate-900 md:text-5xl">
              A daily walk worth <span className="italic text-[#2A6F77]">keeping.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {dailyFeatures.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.1} className="flex">
                <BentoCard testid={`daily-card-${i}`} icon={f.icon} eyebrow={f.eyebrow} title={f.title} accent={i === 1 ? '#2A6F77' : '#B0871F'} className="w-full">
                  {f.body}
                </BentoCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── TRUST METRICS ───────────── */}
      <section data-testid="trust-metrics-section" className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="grid grid-cols-2 divide-slate-200 border-b border-slate-200 py-14 md:grid-cols-4 md:divide-x">
            {stats.map((s) => (
              <div key={s.l} className="px-4 py-4 text-center md:py-0">
                <p className="font-plexmono text-4xl font-medium text-slate-900 md:text-5xl">{s.n}</p>
                <p className="mt-2 font-display text-lg italic text-slate-500">{s.l}</p>
              </div>
            ))}
          </Reveal>
          <Reveal>
            <p className="flex items-center justify-center gap-2 py-6 text-center font-plex text-sm text-slate-500">
              <ScrollText className="h-4 w-4 text-[#B0871F]" />
              Built on trusted, public-domain scholarship — Matthew Henry, John Gill, Jamieson-Fausset-Brown &amp; Adam Clarke.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────── TESTIMONIALS (admin-managed; hidden until approved ones exist) ───────────── */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-section" className="border-b border-slate-200 bg-[#F7F5F0]">
          <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-28">
            <Reveal>
              <Overline className="text-[#B0871F]">Loved by readers</Overline>
              <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-tight tracking-tight text-slate-900 md:text-5xl">
                Words from the community.
              </h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.06}>
                  <figure
                    data-testid={`testimonial-${t.id}`}
                    className="flex h-full flex-col border border-slate-200 bg-white p-7"
                  >
                    <Quote className="h-7 w-7 text-[#B0871F]" />
                    {t.rating > 0 && (
                      <div className="mt-3 font-plexmono text-sm tracking-widest text-[#B0871F]">
                        {'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)}
                      </div>
                    )}
                    <blockquote className="mt-4 flex-1 font-plex text-[15px] leading-relaxed text-slate-700">
                      “{t.quote}”
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                      {t.avatar_url ? (
                        <img src={t.avatar_url} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0F1A2E] font-display text-sm text-[#D4AF37]">
                          {(t.name || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div>
                        <p className="font-display text-base text-slate-900">{t.name}</p>
                        {t.role && <p className="font-plex text-sm text-slate-500">{t.role}</p>}
                      </div>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="bg-[#0F1A2E] text-[#F6F8FB]">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center md:px-12 lg:py-32">
          <Reveal>
            <Overline className="text-[#D4AF37]">Begin today</Overline>
            <h2 className="mx-auto mt-5 max-w-3xl font-display text-5xl font-medium leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
              Experience the Living Word.
            </h2>
            <p className="mx-auto mt-6 max-w-md font-plex text-lg text-slate-400">Open it. Ask. Listen. Grow. Free to start — no credit card required.</p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <button onClick={() => navigate('/bible')} data-testid="final-cta-primary" className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] px-8 py-4 font-plex font-medium text-[#0F1A2E] transition-colors hover:bg-[#c79f2f]">
                <Sparkles className="h-5 w-5" /> Start free now
              </button>
              <button onClick={() => navigate('/login')} data-testid="final-cta-secondary" className="inline-flex items-center justify-center gap-2 border border-slate-600 px-8 py-4 font-plex font-medium text-[#F6F8FB] transition-colors hover:border-[#F6F8FB]">
                Sign in
              </button>
              <InstallAppButton
                testId="final-cta-install"
                label="Install App"
                className="inline-flex items-center justify-center gap-2 border border-slate-600 px-8 py-4 font-plex font-medium text-[#F6F8FB] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── FOOTER ───────────── */}
      <footer data-testid="footer-section" className="bg-[#F6F8FB]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-12 md:flex-row md:px-12">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[#B0871F]" />
            <span className="font-display text-xl font-semibold text-slate-900">Lumera</span>
            <span className="font-plex text-sm text-slate-500">— Experience the Living Word</span>
          </div>
          <div className="flex gap-8 font-plex text-sm text-slate-500">
            <button onClick={() => navigate('/collab')} data-testid="footer-collab-link" className="font-medium text-[#B0871F] transition-colors hover:text-[#9A731A]">Collaborate</button>
            <button className="transition-colors hover:text-[#B0871F]">Privacy</button>
            <button className="transition-colors hover:text-[#B0871F]">Terms</button>
            <button className="transition-colors hover:text-[#B0871F]">Contact</button>
          </div>
          <p className="font-plexmono text-[11px] uppercase tracking-widest text-slate-400">© 2026 Lumera</p>
        </div>
      </footer>

      {/* ───────────── STICKY MOBILE CTA BAR ───────────── */}
      <div
        data-testid="mobile-sticky-cta"
        className="fixed inset-x-0 bottom-0 z-50 flex gap-3 border-t border-slate-200 bg-white/95 p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] backdrop-blur-xl lg:hidden"
      >
        <button
          onClick={() => navigate('/bible')}
          data-testid="mobile-cta-open"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#B0871F] px-5 py-3 font-plex text-sm font-semibold text-white transition-colors hover:bg-[#9A731A]"
        >
          Open the Word <ArrowRight className="h-4 w-4" />
        </button>
        <InstallAppButton
          testId="mobile-cta-install"
          label="Install App"
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-300 px-5 py-3 font-plex text-sm font-medium text-slate-800 transition-colors hover:border-[#B0871F] hover:text-[#B0871F]"
        />
      </div>
    </div>
  );
};

export default NewLandingPage;
